import React, { useState, useEffect } from "react";
import MaterialListSelect from "./MaterialListSelect";
import MaterialGroupSelect from "./MaterialGroupSelect";
import MaterialListModal from "./modals/MaterialListModal";
import MaterialLocationSelect from "./MaterialLocationSelect";
import axios from "axios";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";

const VIEW_OPTIONS = [
  { value: "0", label: "Show All" },
  { value: "1", label: "Search by Product Code / Name" },
  { value: "2", label: "Search by Group" },
];

const thCls =
  "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-r last:border-r-0";
const tdCls =
  "px-4 py-2.5 text-xs text-gray-700 border-r last:border-r-0 whitespace-nowrap";

function MaterialList() {
  const [matList, setMatList] = useState([]);
  const [selectedMatList, setSelectedMatList] = useState(null);
  const [selectedMatGroup, setSelectedMatGroup] = useState(null);
  const [groupedMaterialList, setGroupedMaterialList] = useState({});
  const [viewType, setViewType] = useState("0");
  const [productLocation, setProductLocation] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const formatRecord = (data) => {
    if (!data || data.length === 0) return {};
    return data.reduce((acc, item) => {
      const g = item.group_name;
      if (!acc[g]) acc[g] = [];
      acc[g].push(item);
      return acc;
    }, {});
  };

  const handleViewTypeChange = (e) => {
    setViewType(e.target.value);
    setMatList([]);
    setGroupedMaterialList({});
    setSelectedMatList(null);
    setSelectedMatGroup(null);
    setSearched(false);
  };

  const getallMaterialList = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        "https://eps.elkomprintama.com/get/api/store/mats",
      );
      setGroupedMaterialList(formatRecord(res.data.result));
      setSearched(true);
    } catch {
      toast.error("Failed to fetch material list.");
    } finally {
      setIsLoading(false);
    }
  };

  const getGroupedMaterialList = async (groupId) => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/mats/groups/${groupId}`,
      );
      setMatList(res.data.result);
      setSearched(true);
    } catch {
      toast.error("Failed to fetch group material list.");
    } finally {
      setIsLoading(false);
    }
  };

  const getMaterialListById = async (productId) => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/mats/${productId}`,
      );
      const data = res.data.result;
      setMatList(
        typeof data === "object" && !Array.isArray(data) ? [data] : data,
      );
      setSearched(true);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to fetch material.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (viewType === "1" && !selectedMatList) {
      toast.warning("Please select a product.");
      return;
    }
    if (viewType === "2" && !selectedMatGroup) {
      toast.warning("Please select a group.");
      return;
    }
    if (viewType === "0") getallMaterialList();
    else if (viewType === "1") getMaterialListById(selectedMatList);
    else if (viewType === "2") getGroupedMaterialList(selectedMatGroup);
  };

  const refreshData = () => {
    if (viewType === "0") getallMaterialList();
    else if (viewType === "1") getMaterialListById(selectedMatList);
    else if (viewType === "2") getGroupedMaterialList(selectedMatGroup);
  };

  const groupEntries = Object.entries(groupedMaterialList);
  const totalItems =
    viewType === "0"
      ? groupEntries.reduce((s, [, items]) => s + items.length, 0)
      : matList.length;

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {isLoading && <LoadingOverlay message="Loading…" />}

      {/* Header */}
      <div className="w-full rounded-lg bg-cyan-700 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Material List
        </label>
      </div>

      {/* Body card */}
      <div className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Controls */}
        <form
          onSubmit={handleSubmit}
          className="flex-shrink-0 px-6 py-5 border-b border-gray-100 space-y-4"
        >
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              View Mode
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-cyan-400 bg-white"
              value={viewType}
              onChange={handleViewTypeChange}
            >
              {VIEW_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {viewType === "1" && (
            <div className="space-y-3">
              <MaterialLocationSelect onChangeList={setProductLocation} />
              <MaterialListSelect
                onChangeList={(Arr) => setSelectedMatList(Arr[0])}
                onOffSwitch={viewType}
                location_id={productLocation}
              />
            </div>
          )}

          {viewType === "2" && (
            <MaterialGroupSelect onChangeList={setSelectedMatGroup} />
          )}

          <button
            type="submit"
            className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-cyan-700 hover:bg-cyan-800 transition"
          >
            Show Materials
          </button>
        </form>

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-auto">
          {!searched ? (
            <div className="h-full flex items-center justify-center text-gray-400 italic text-sm">
              Select a view mode and click Show Materials
            </div>
          ) : totalItems === 0 ? (
            <div className="h-full flex items-center justify-center text-gray-400 italic text-sm">
              No materials found
            </div>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 bg-gray-700 text-white uppercase">
                <tr>
                  <th className={thCls}>No</th>
                  <th className={thCls}>Product Code</th>
                  <th className={thCls}>Product Name</th>
                  <th className={thCls}>Group</th>
                  <th className={thCls}>Unit</th>
                  <th className={thCls}>Location</th>
                </tr>
              </thead>
              <tbody>
                {viewType === "0"
                  ? groupEntries.map(([groupName, groupItems], gi) => (
                      <React.Fragment key={`g-${gi}`}>
                        <tr className="bg-cyan-700 border-t-4 border-cyan-400">
                          <td
                            colSpan={6}
                            className="px-4 py-2 text-xs font-bold text-white uppercase tracking-widest"
                          >
                            {groupName} · {groupItems.length} item
                            {groupItems.length !== 1 ? "s" : ""}
                          </td>
                        </tr>
                        {groupItems.map((data, i) => (
                          <tr
                            key={`c-${gi}-${i}`}
                            onDoubleClick={() => {
                              setShowModal(true);
                              setModalData(data);
                            }}
                            className={`border-b cursor-pointer hover:bg-cyan-50 ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                          >
                            <td className={`${tdCls} text-gray-400`}>
                              {i + 1}
                            </td>
                            <td className={tdCls}>{data.product_code}</td>
                            <td
                              className={`${tdCls} font-medium text-gray-800`}
                            >
                              {data.product_name}
                            </td>
                            <td className={tdCls}>{data.group_name}</td>
                            <td className={tdCls}>{data.unit}</td>
                            <td className={tdCls}>
                              {data.product_location_name}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))
                  : matList.map((data, i) => (
                      <tr
                        key={data.product_id || i}
                        onDoubleClick={() => {
                          setShowModal(true);
                          setModalData(data);
                        }}
                        className={`border-b cursor-pointer hover:bg-cyan-50 ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                      >
                        <td className={`${tdCls} text-gray-400`}>{i + 1}</td>
                        <td className={tdCls}>{data.product_code}</td>
                        <td className={`${tdCls} font-medium text-gray-800`}>
                          {data.product_name}
                        </td>
                        <td className={tdCls}>{data.group_name}</td>
                        <td className={tdCls}>{data.unit}</td>
                        <td className={tdCls}>{data.product_location_name}</td>
                      </tr>
                    ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer count */}
        {searched && totalItems > 0 && (
          <div className="flex-shrink-0 px-5 py-2.5 border-t border-gray-100 bg-gray-50">
            <span className="text-xs text-gray-400">
              {totalItems} material{totalItems !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <MaterialListModal
            dataSet={modalData}
            onClose={() => setShowModal(false)}
            refreshData={refreshData}
          />
        </div>
      )}
    </div>
  );
}

export default MaterialList;
